import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { UrlReplacerService } from './url-replacer.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UrlOptionsByOfferId } from './types';

@Component({
  selector: 'gtd-urls-replacing-search',
  templateUrl: './urls-replacing-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UrlsReplacingSearchComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UrlsReplacingSearchComponent),
      multi: true,
    },
    UrlReplacerService,
  ],
})
export class UrlsReplacingSearchComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() public oldDomainControl: FormControl<string>;

  public searchOnLoading$ = new BehaviorSubject<boolean>(false);
  public urlsGroupByOffer$: Observable<UrlOptionsByOfferId | null> = this.urlReplacerService.optionsGroupedByOfferId$;
  public newDomainControl = new FormControl<string>('', Validators.required);
  public onChange: (value: any) => void = () => {};
  public onTouched = () => {};

  constructor(private urlReplacerService: UrlReplacerService) {}

  public ngOnInit(): void {
    this.oldDomainControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        map((url) => {
          const domain = url?.replace(/^(http|https):\/\//, '');
          if (!domain) {
            return null;
          }
          return url;
        }),
        tap((url) => this.onChange(url)),
        switchMap((url: string) => {
          if (url) {
            this.searchOnLoading$.next(true);
          }
          return this.urlReplacerService.fetchOptions(url);
        })
      )
      .subscribe((response) => {
        if (response && response.data.options.length === 0) {
          this.onChange(null);
        }
        this.searchOnLoading$.next(false);
      });
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(): void {}

  public validate(): ValidationErrors | null {
    const url = this.oldDomainControl.value?.replace(/^(http|https):\/\//, '');
    if (!url) {
      return { required: true };
    }
    return this.oldDomainControl.errors;
  }
}
